// src/pages/Menu/Vinculation/CRM/Indicators.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Users2,
  Activity,
  AlertTriangle,
  Crown,
  TrendingDown,
  RefreshCw,
  Calendar,
  BarChart3,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  XCircle,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { Banner } from '../../../../components/common/Banner';
import TimelineFilter from '../../../../components/common/TimelineFilter';
import SectionTips from '../../../../components/common/SectionTips';
import TooltipHelper from '../../../../components/common/TooltipHelper';
import HistoricalChartModal from './HistoricalChartModal';
import { crmAnalytics, crmListSummary } from '../../../../services/vinculationCRMService';
import { getContactsListByCompanySafe } from '../../../../services/contactsExportService';
import { useAuth } from '../../../../context/authContext';
import ArrowBack from '../../../../components/common/ArrowBack';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/* -------- helpers -------- */
const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const cleanDateString = dateString.split('T')[0];
  const parts = cleanDateString.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

const fmt = (n) => new Intl.NumberFormat('es-CL').format(Number.isFinite(n) ? n : 0);
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysBetween = (iso) => {
  if (!iso) return null; // null en vez de Infinity para mejor manejo
  const a = parseDateToLocal(todayISO());
  const b = parseDateToLocal(iso);
  if (!a || !b) return null;
  const days = Math.floor((a - b) / 86400000);
  return days >= 0 ? days : null; // Si la fecha es futura, retornar null
};
const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = parseDateToLocal(iso);
    if (!d || Number.isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return '—';
  }
};
const formatDays = (days) => {
  if (days === null || days === undefined || !Number.isFinite(days) || days === 9999) {
    return 'Sin reuniones';
  }
  if (days < 30) return `${days} días`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} mes${months > 1 ? 'es' : ''}`;
  }
  const years = Math.floor(days / 365);
  const remainingMonths = Math.floor((days % 365) / 30);
  if (remainingMonths === 0) {
    return `${years} año${years > 1 ? 's' : ''}`;
  }
  return `${years} año${years > 1 ? 's' : ''} ${remainingMonths} mes${remainingMonths > 1 ? 'es' : ''}`;
};
const isAdminOrOwner = (c) => {
  const role = (c?.role || '').toLowerCase();
  const name = (c?.trade_name || c?.business_name || '').toLowerCase();
  return role === 'admin' || name.includes('cidere');
};
const normalizeCompany = (c) => ({
  rut: c.rut ?? c.rutEmpresa ?? '',
  name: c.trade_name?.trim() ? c.trade_name : c.business_name || 'Sin nombre',
  business_name: c.business_name ?? '',
  trade_name: c.trade_name ?? '',
  industry: c.industry ?? '',
  region: c.region ?? '',
  district: c.district ?? '',
  logo: c.logo ?? c.logo_url ?? null,
  meetingsCount: Number(c.meetingsCountYear ?? c.meetingsCount ?? 0),
  lastMeetingAt: c.lastMeetingAt ?? null,
  role: (c.role ?? '').toString().toLowerCase(),
  entry_date: c.entry_date ?? null,
});
const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const yearFromISO = (iso) => {
  if (!iso) return null;
  const d = parseDateToLocal(iso);
  if (!d) return null;
  const y = d.getFullYear();
  return Number.isFinite(y) ? y : null;
};
const computeMaxYearFromSummary = (summary) => {
  const now = new Date().getFullYear();
  const years = (Array.isArray(summary) ? summary : [])
    .map((c) => yearFromISO(c?.lastMeetingAt))
    .filter(Boolean);
  return years.length ? Math.max(now, ...years) : now;
};

/* -------- UI: tarjetas -------- */
/* (sin anidar <button>) */
const StatsCard = ({ title, value, icon: Icon, gradient, hint, onClick, right, dateRange }) => {
  // Determinar colores basados en el tipo de tarjeta
  const isGradient = gradient.includes('gradient');
  const isPrimary = gradient.includes('[#0488AF]') || gradient.includes('[#09274A]');
  const isAmber = gradient.includes('amber');

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined
      }
      className={`text-left ${gradient} p-6 rounded-2xl shadow-lg hover:shadow-xl w-full transition-all duration-300 relative overflow-hidden
                 ${onClick ? 'cursor-pointer transform hover:scale-[1.02]' : 'cursor-default'}`}
    >
      {/* Efecto de brillo sutil */}
      {isGradient && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
      )}

      <div className="relative flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {!isGradient && (
              <div
                className={`p-2 rounded-lg ${
                  isPrimary
                    ? 'bg-[#0488AF]/10 text-[#0488AF]'
                    : isAmber
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
              </div>
            )}
            <div>
              <div
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isGradient
                    ? 'text-white/90'
                    : isAmber
                    ? 'text-amber-700'
                    : 'text-gray-600'
                }`}
              >
                {title}
              </div>
              {hint && (
                <div
                  className={`text-[10px] mt-0.5 ${
                    isGradient ? 'text-white/70' : 'text-gray-500'
                  }`}
                >
                  {hint}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3">
            <div
              className={`text-4xl font-extrabold leading-none ${
                isGradient ? 'text-white drop-shadow-md' : 'text-gray-900'
              }`}
            >
              {fmt(value)}
            </div>
            <div
              className={`text-xs font-medium mt-1.5 ${
                isGradient ? 'text-white/90' : 'text-gray-600'
              }`}
            >
              {title.toLowerCase()}
            </div>
            {dateRange && (
              <div
                className={`text-[10px] font-normal mt-1 ${
                  isGradient ? 'text-white/80' : 'text-gray-500'
                }`}
              >
                {dateRange}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {isGradient ? (
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30">
              <Icon className="w-8 h-8 text-white drop-shadow-md" />
            </div>
          ) : (
            <div
              className={`p-3 rounded-xl ${
                isPrimary
                  ? 'bg-gradient-to-br from-[#0488AF] to-[#09274A] text-white shadow-lg'
                  : isAmber
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Icon className="w-8 h-8" />
            </div>
          )}
          {right}
        </div>
      </div>

      {/* Línea decorativa inferior */}
      {!isGradient && (
        <div
          className={`absolute bottom-0 left-0 right-0 h-1 ${
            isPrimary
              ? 'bg-gradient-to-r from-[#0488AF] to-[#09274A]'
              : isAmber
              ? 'bg-gradient-to-r from-amber-400 to-orange-500'
              : 'bg-gray-300'
          }`}
        />
      )}
    </div>
  );
};

/* -------- Barchart simple: Totales por año (compacto) -------- */
const YearTotalsChart = ({ series, onShowHistorical }) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  const max = Math.max(...series.map((s) => num(s.count, 0)), 1);
  
  // Ajustar columnas dinámicamente según la cantidad de años
  const getCols = (length) => {
    if (length >= 10) return 'lg:grid-cols-10';
    if (length >= 9) return 'lg:grid-cols-9';
    if (length >= 8) return 'lg:grid-cols-8';
    if (length >= 7) return 'lg:grid-cols-7';
    if (length >= 6) return 'lg:grid-cols-6';
    if (length >= 5) return 'lg:grid-cols-5';
    if (length >= 4) return 'lg:grid-cols-4';
    if (length >= 3) return 'lg:grid-cols-3';
    return 'lg:grid-cols-2';
  };
  
  const cols = getCols(series.length);
  // Reducir altura cuando hay muchos años para hacerlo más compacto
  const chartHeight = series.length > 7 ? 'h-32' : 'h-40';

  return (
    <div className="bg-white border rounded-xl p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-[#09274A] text-sm">
          Reuniones por año (comparativo)
        </div>
        {onShowHistorical && (
          <button
            onClick={onShowHistorical}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0488AF] hover:text-[#037a98] hover:bg-[#0488AF]/10 rounded-lg transition-colors border border-[#0488AF]/30 hover:border-[#0488AF]/50"
            title="Ver histórico completo"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Ver histórico
          </button>
        )}
      </div>
      <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 ${cols} gap-1.5`}>
        {series.map(({ year, count }) => {
          const h = Math.round((num(count, 0) / max) * 100);
          return (
            <div key={year} className={`flex flex-col items-center justify-end ${chartHeight}`}>
              <div
                className="w-full h-full flex items-end"
                title={`${year}: ${fmt(count)} reuniones`}
              >
                <div
                  className="w-full bg-[#0488AF]/85 rounded-t"
                  style={{ height: `${h}%` }}
                />
              </div>
              <div className="mt-1 text-[10px] text-gray-700 font-medium">{year}</div>
              <div className="text-[9px] text-gray-500">{fmt(count)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* =================== Page =================== */
export default function CRMIndicators() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const myRutRaw = (user?.rut || localStorage.getItem('rut') || '').toString();
  const myRut = myRutRaw.replace(/\./g, '').replace(/-/g, '').trim();

  const [year, setYear] = useState(''); // año seleccionado ('', '2025', etc.)
  const [maxDataYear, setMaxDataYear] = useState(new Date().getFullYear());
  const [yearOptions, setYearOptions] = useState(['']); // opciones dinámicas del selector

  // Mes seleccionado (1-12, solo activo cuando hay año)
  const [month, setMonth] = useState(''); // '', '1', '2', ..., '12' o '1-9' para rango

  // Rango de fechas (desde/hasta)
  const [yearFrom, setYearFrom] = useState('');
  const [monthFrom, setMonthFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [monthTo, setMonthTo] = useState('');

  const [loading, setLoading] = useState(true);
  const [ana, setAna] = useState(null);
  const [sum, setSum] = useState([]);
  const [excludeRut, setExcludeRut] = useState();
  const [allCompanies, setAllCompanies] = useState([]); // Todas las empresas sin filtrar por reuniones

  // Modales
  const [showInactive, setShowInactive] = useState(false);
  const [showWithMeet, setShowWithMeet] = useState(false);
  const [showTopAll, setShowTopAll] = useState(false);
  const [showBottomAll, setShowBottomAll] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showHistoricalChart, setShowHistoricalChart] = useState(false);

  // Serie de años (comparativo)
  const [yearSeries, setYearSeries] = useState([]);
  const [initialized, setInitialized] = useState(false);

  // Cargar todas las empresas (sin filtrar por reuniones) para estadísticas
  const loadAllCompanies = async () => {
    try {
      const list = await crmListSummary(undefined); // Sin filtros
      let norm = (Array.isArray(list) ? list : []).map(normalizeCompany);

      // excluir admins/propia empresa
      norm = norm.filter((c) => {
        const rutNorm = (c.rut || '').toString().replace(/\./g, '').replace(/-/g, '').trim();
        const isMine = !!myRut && rutNorm === myRut;
        const looksOwner = (c.trade_name || c.name || '').toString().toLowerCase().includes('cidere');
        return c.role !== 'admin' && !isMine && !looksOwner;
      });

      setAllCompanies(norm);
    } catch (e) {
      setAllCompanies([]);
    }
  };

  // Construye serie multi-año (base + 4 previos)
  const buildYearSeries = async (exRut, base, rangeFilters = null, yearFilter = null, monthFilter = null) => {
    let baseYear = base ?? new Date().getFullYear();
    let years = [];
    
    // Si hay filtros de rango, expandir para mostrar todos los años del rango más algunos anteriores
    if (rangeFilters && rangeFilters.yearFrom && rangeFilters.monthFrom && rangeFilters.yearTo && rangeFilters.monthTo) {
      const yearFromNum = Number(rangeFilters.yearFrom);
      const yearToNum = Number(rangeFilters.yearTo);
      const yearSpan = yearToNum - yearFromNum;
      // Mostrar el rango completo más 2 años anteriores para contexto
      const startYear = Math.max(yearFromNum - 2, yearFromNum);
      const endYear = yearToNum;
      years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);
      baseYear = yearToNum;
    } else if (yearFilter) {
      // Si hay un año seleccionado, mostrar ese año más 4 años anteriores
      const selectedYear = Number(yearFilter);
      years = Array.from({ length: 5 }, (_, i) => selectedYear - i);
      baseYear = selectedYear;
    } else {
      // Sin filtros, comportamiento normal: últimos 5 años
      years = Array.from({ length: 5 }, (_, i) => baseYear - i);
    }
    
    const results = await Promise.all(
      years.map((y) => {
        // Si hay filtros de rango, aplicarlos a cada año
        if (rangeFilters && rangeFilters.yearFrom && rangeFilters.monthFrom && rangeFilters.yearTo && rangeFilters.monthTo) {
          // Para cada año en la serie, aplicar el mismo rango de meses si el año está dentro del rango
          const yearNum = Number(y);
          const yearFromNum = Number(rangeFilters.yearFrom);
          const yearToNum = Number(rangeFilters.yearTo);
          
          // Si el año está dentro del rango, aplicar los filtros de mes
          if (yearNum >= yearFromNum && yearNum <= yearToNum) {
            // Si es el año inicial, usar monthFrom; si es el año final, usar monthTo; si está en medio, usar todo el año
            let monthFrom = null;
            let monthTo = null;
            
            if (yearNum === yearFromNum && yearNum === yearToNum) {
              // Mismo año: usar el rango completo de meses
              monthFrom = Number(rangeFilters.monthFrom);
              monthTo = Number(rangeFilters.monthTo);
            } else if (yearNum === yearFromNum) {
              // Año inicial: desde monthFrom hasta diciembre
              monthFrom = Number(rangeFilters.monthFrom);
              monthTo = 12;
            } else if (yearNum === yearToNum) {
              // Año final: desde enero hasta monthTo
              monthFrom = 1;
              monthTo = Number(rangeFilters.monthTo);
            } else {
              // Años intermedios: todo el año (sin filtro de mes)
              return crmAnalytics({ year: y, excludeRut: exRut });
            }
            
            return crmAnalytics({ 
              year: y, 
              monthFrom: monthFrom,
              monthTo: monthTo,
              excludeRut: exRut 
            });
          } else {
            // Año fuera del rango: no aplicar filtros de mes
            return crmAnalytics({ year: y, excludeRut: exRut });
          }
        } else if (yearFilter && monthFilter) {
          // Si hay un año y mes seleccionado, aplicar el filtro de mes solo al año seleccionado
          const yearNum = Number(y);
          const selectedYear = Number(yearFilter);
          
          if (yearNum === selectedYear) {
            // Si el mes es un rango (ej: "1-9")
            if (monthFilter.includes('-')) {
              const [fromMonth, toMonth] = monthFilter.split('-').map(Number);
              return crmAnalytics({ 
                year: y, 
                monthFrom: fromMonth,
                monthTo: toMonth,
                excludeRut: exRut 
              });
            } else {
              // Mes individual
              return crmAnalytics({ 
                year: y, 
                month: Number(monthFilter),
                excludeRut: exRut 
              });
            }
          } else {
            // Otros años: sin filtro de mes
            return crmAnalytics({ year: y, excludeRut: exRut });
          }
        } else {
          // Sin filtros de rango, comportamiento normal
          return crmAnalytics({ year: y, excludeRut: exRut });
        }
      })
    );
    return years
      .map((y, i) => ({ year: y, count: num(results[i]?.totals?.meetings, 0) }))
      .reverse();
  };

  // Inicializar año automáticamente solo una vez al montar
  useEffect(() => {
    if (!initialized && !year && !yearFrom) {
      const initYear = async () => {
        try {
          const s0 = await crmListSummary(undefined);
          const maxY = computeMaxYearFromSummary(s0);
          setMaxDataYear(maxY);
          setYearOptions(Array.from({ length: 7 }, (_, i) => maxY - i));
          setYear(String(maxY));
          setInitialized(true);
        } catch (e) {
          console.error('Error initializing year:', e);
          setInitialized(true);
        }
      };
      initYear();
    } else if (!initialized) {
      // Si ya hay un año o rango seleccionado, marcar como inicializado
      setInitialized(true);
    }
  }, [initialized, year, yearFrom]);

  const load = async () => {
    setLoading(true);
    try {
      // 1) Cargar summary sin filtros para obtener año máximo, opciones y admin
      const s0 = await crmListSummary(undefined);
      const admin = (Array.isArray(s0) ? s0 : []).find(isAdminOrOwner);
      const ex = admin?.rut || undefined;
      setExcludeRut(ex);

      // 2) Determinar año máximo y actualizar opciones (si no está inicializado)
      if (!initialized) {
        const maxY = computeMaxYearFromSummary(s0);
        setMaxDataYear(maxY);
        setYearOptions(Array.from({ length: 7 }, (_, i) => maxY - i));
        setInitialized(true);
      }

      // 3) Usar el año actual (puede ser el seleccionado o el máximo si no hay selección)
      const currentYear = year || (maxDataYear ? String(maxDataYear) : '');

      // Si no hay año y no hay rango, no cargar datos aún (esperar a que se inicialice)
      if (!currentYear && !yearFrom) {
        setLoading(false);
        return;
      }

      // 4) Construir parámetros de filtro
      const params = {};
      const analyticsParams = {};

      // Si hay rango de fechas (desde/hasta), usar rango
      if (yearFrom && monthFrom && yearTo && monthTo) {
        params.yearFrom = Number(yearFrom);
        params.monthFrom = Number(monthFrom);
        params.yearTo = Number(yearTo);
        params.monthTo = Number(monthTo);
        analyticsParams.yearFrom = Number(yearFrom);
        analyticsParams.monthFrom = Number(monthFrom);
        analyticsParams.yearTo = Number(yearTo);
        analyticsParams.monthTo = Number(monthTo);
      } else if (currentYear) {
        // Si hay año individual, usar filtro de año/mes
        const yearNum = Number(currentYear);
        params.year = yearNum;
        analyticsParams.year = yearNum;

        if (month && currentYear) {
          // Si month tiene formato "1-9" (rango), parsearlo
          const monthParts = month.split('-');
          if (monthParts.length === 2) {
            // Rango de meses: "1-9" significa enero a septiembre
            params.monthFrom = Number(monthParts[0]);
            params.monthTo = Number(monthParts[1]);
            analyticsParams.monthFrom = Number(monthParts[0]);
            analyticsParams.monthTo = Number(monthParts[1]);
          } else {
            // Mes individual
            params.month = Number(month);
            analyticsParams.month = Number(month);
          }
        }
      }

      // 5) Cargar analytics + summary (metadatos) con los filtros aplicados
      const finalAnalyticsParams =
        Object.keys(analyticsParams).length > 0
          ? { ...analyticsParams, excludeRut: ex }
          : { excludeRut: ex };
      const finalSummaryParams =
        Object.keys(params).length > 0 ? { ...params, excludeRut: ex } : { excludeRut: ex };

      const [a, s] = await Promise.all([
        crmAnalytics(finalAnalyticsParams),
        crmListSummary(finalSummaryParams),
      ]);

      const sanitizedSummary = (Array.isArray(s) ? s : []).map((c) => ({
        ...c,
        meetingsCountYear: num(c?.meetingsCountYear, 0),
        meetingsCount: num(c?.meetingsCount, 0),
        lastMeetingAt: c?.lastMeetingAt || null,
        trade_name: c?.trade_name || null,
        business_name: c?.business_name || null,
        industry: c?.industry || null,
        region: c?.region || null,
        district: c?.district || null,
      }));

      const normalizedAnalytics = {
        ...a,
        totals: {
          companies: num(a?.totals?.companies, 0),
          meetings: num(a?.totals?.meetings, 0),
          activeCompanies: num(a?.totals?.activeCompanies, 0),
          inactive60: num(a?.totals?.inactive60, 0),
          linkLevels: a?.totals?.linkLevels ?? { high: 0, medium: 0, low: 0 },
        },
        ranking: Array.isArray(a?.ranking) ? a.ranking : [],
        inactiveList: Array.isArray(a?.inactiveList)
          ? a.inactiveList
          : Array.isArray(a?.inactiveList60)
          ? a.inactiveList60
          : undefined,
      };

      setAna(normalizedAnalytics);
      setSum(sanitizedSummary);

      // 6) Serie comparativa multi-año basada en year o en maxDataYear (cambia con filtros)
      // Si hay rango, usar yearTo como base; si no, usar currentYear o maxDataYear
      const baseYear = (yearFrom && yearTo) 
        ? Number(yearTo) 
        : currentYear 
          ? Number(currentYear) 
          : yearFrom 
            ? Number(yearFrom) 
            : maxDataYear;
      // Pasar filtros de rango si existen
      const rangeFilters = (yearFrom && monthFrom && yearTo && monthTo) 
        ? { yearFrom, monthFrom, yearTo, monthTo }
        : null;
      // Pasar filtros de año/mes individual si existen
      const yearFilter = year || null;
      const monthFilter = month || null;
      const series = await buildYearSeries(ex, baseYear, rangeFilters, yearFilter, monthFilter);
      setYearSeries(series);
    } catch (e) {
      console.error('Error loading CRM indicators:', e);
      Swal.fire('Error', e.message || 'No fue posible cargar los indicadores', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllCompanies();
  }, [myRut]); // Cargar todas las empresas una vez

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, yearFrom, monthFrom, yearTo, monthTo]);

  /* ======== DERIVADOS ======== */

  // Empresas en sistema: contar acumulativamente según fecha de ingreso
  // Si hay un año seleccionado, cuenta todas las empresas que se unieron hasta ese año (incluidas las anteriores)
  const companies = useMemo(() => {
    // Si hay un rango de fechas, contar empresas que entraron hasta el final del rango (acumulativo)
    if (yearFrom && monthFrom && yearTo && monthTo) {
      return allCompanies.filter((c) => {
        if (!c.entry_date) {
          // Si no tiene fecha de ingreso, incluirla (empresas antiguas sin fecha)
          return true;
        }
        const d = parseDateToLocal(c.entry_date);
        if (!d) return true; // Incluir si no se puede parsear
        
        const entryYear = d.getFullYear();
        const entryMonth = d.getMonth() + 1; // getMonth() es 0-indexed
        
        const toYear = Number(yearTo);
        const toMonth = Number(monthTo);
        
        // Incluir todas las empresas que se unieron hasta el final del rango (acumulativo)
        if (entryYear < toYear) return true;
        if (entryYear === toYear && entryMonth <= toMonth) return true;
        
        return false;
      }).length;
    }
    // Si hay un año seleccionado (no rango), contar empresas que se unieron hasta ese año (acumulativo)
    if (year && !yearFrom) {
      const selectedYear = Number(year);
      return allCompanies.filter((c) => {
        if (!c.entry_date) {
          // Si no tiene fecha de ingreso, incluirla (empresas antiguas sin fecha)
          return true;
        }
        const d = parseDateToLocal(c.entry_date);
        if (!d) return true; // Incluir si no se puede parsear
        
        const entryYear = d.getFullYear();
        // Incluir todas las empresas que se unieron hasta el año seleccionado (acumulativo)
        return entryYear <= selectedYear;
      }).length;
    }
    // Si no hay filtros, usar el total de todas las empresas
    return (
      allCompanies.length ||
      num(ana?.totals?.companies, ana?.ranking?.length ?? 0)
    );
  }, [allCompanies, year, yearFrom, monthFrom, yearTo, monthTo, ana]);

  const rankingNorm = useMemo(() => {
    const meta = new Map(sum.map((c) => [c.rut, c]));
    return (ana?.ranking || []).map((r) => {
      const m = meta.get(r.rut) || {};
      return {
        rut: r.rut,
        name: r.name || m.trade_name || m.business_name || r.rut,
        industry: r.industry ?? m.industry ?? null,
        region: r.region ?? m.region ?? null,
        district: r.district ?? m.district ?? null,
        lastMeetingAt: r.lastMeetingAt ?? m.lastMeetingAt ?? null,
        count: num(r.count, 0),
      };
    });
  }, [ana, sum]);

  // Con reuniones (año)
  const withList = useMemo(
    () => rankingNorm.filter((r) => r.count > 0).sort((a, b) => b.count - a.count),
    [rankingNorm]
  );
  const withMeet = withList.length || num(ana?.totals?.activeCompanies, 0);

  // Inactivas ≥ 60 días
  const inactivas = useMemo(() => {
    const fromAPI = Array.isArray(ana?.inactiveList)
      ? ana.inactiveList
      : Array.isArray(ana?.inactiveList60)
      ? ana.inactiveList60
      : null;
    if (fromAPI && fromAPI.length) {
      return fromAPI
        .map((x) => {
          // Si days es 9999, significa empresa sin reuniones - mantenerlo como 9999
          // Si days es Infinity o no es finito pero hay lastMeetingAt, calcularlo
          let days = x.days;
          if (days === 9999) {
            // Empresa sin reuniones - mantener 9999
            return { ...x, lastMeetingAt: null, days: 9999 };
          }
          if (!Number.isFinite(days) || days === Infinity) {
            days = x.lastMeetingAt ? daysBetween(x.lastMeetingAt) : null;
          }
          return { ...x, lastMeetingAt: x.lastMeetingAt ?? null, days };
        })
        // Incluir empresas con days >= 60 o days === 9999 (sin reuniones)
        .filter((x) => x.days !== null && (x.days >= 60 || x.days === 9999))
        .sort((a, b) => {
          // Ordenar: 9999 al final, luego por días descendente
          if (a.days === 9999 && b.days !== 9999) return 1;
          if (a.days !== 9999 && b.days === 9999) return -1;
          return (b.days ?? 0) - (a.days ?? 0);
        });
    }
    // Fallback: calcular desde rankingNorm
    return rankingNorm
      .map((r) => {
        const days = daysBetween(r.lastMeetingAt);
        // Si no hay reuniones, considerar como 9999
        return { ...r, days: days === null ? 9999 : days };
      })
      // Incluir empresas con days >= 60 o days === 9999 (sin reuniones)
      .filter((x) => x.days !== null && (x.days >= 60 || x.days === 9999))
      .sort((a, b) => {
        // Ordenar: 9999 al final, luego por días descendente
        if (a.days === 9999 && b.days !== 9999) return 1;
        if (a.days !== 9999 && b.days === 9999) return -1;
        return (b.days ?? 0) - (a.days ?? 0);
      });
  }, [ana, rankingNorm]);

  // Usar siempre la longitud de la lista filtrada para que coincida con el modal
  const inactiveCount = inactivas.length;

  const totalMeetings = useMemo(() => {
    const fromAnalytics = num(ana?.totals?.meetings, -1);
    if (fromAnalytics >= 0) return fromAnalytics;
    return rankingNorm.reduce((acc, r) => acc + num(r.count, 0), 0);
  }, [ana, rankingNorm]);

  const topPreview = useMemo(() => {
    if (Array.isArray(ana?.top) && ana.top.length) {
      return ana.top
        .map((t) => ({ ...t, count: num(t.count, 0) }))
        .filter((t) => t.count > 0)
        .slice(0, 5);
    }
    return withList.slice(0, 5);
  }, [ana, withList]);

  const bottomRisk = useMemo(
    () =>
      (ana?.ranking || [])
        .map((r) => ({ ...r, count: num(r.count, 0) }))
        .filter((r) => r.count <= 1)
        .slice(0, 5),
    [ana]
  );

  // Función para exportar empresas con bajo vínculo (0-1 reuniones) a Excel
  const handleExportLowLinkCompanies = async () => {
    try {
      const lowLinkCompanies = (ana?.ranking || [])
        .map((r) => ({ ...r, count: num(r.count, 0) }))
        .filter((r) => r.count <= 1)
        .sort((a, b) => a.count - b.count);

      if (lowLinkCompanies.length === 0) {
        Swal.fire('Sin datos', 'No hay empresas con bajo vínculo para exportar.', 'info');
        return;
      }

      // Mostrar confirmación con información
      const confirmResult = await Swal.fire({
        title: '¿Exportar lista de socios?',
        html: `
          <div class="text-left">
            <p class="mb-3">Se exportará la <strong>lista completa de socios con bajo vínculo (0-1 reuniones) ordenada por cantidad de reuniones</strong>.</p>
            <p class="text-sm text-gray-600">El archivo Excel incluirá todos los socios con bajo vínculo en el período seleccionado.</p>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, exportar',
        cancelButtonText: 'Cancelar',
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      // Mostrar modal de carga
      const loadingToast = Swal.fire({
        title: 'Exportando lista de socios con bajo vínculo...',
        html: 'Generando archivo Excel con la lista completa de socios con bajo vínculo (0-1 reuniones) ordenada por cantidad de reuniones. Por favor espera.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const workbook = new ExcelJS.Workbook();

      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0488AF' },
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        },
      };

      const titleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF09274A' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      };

      const dataStyle = {
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        },
      };

      const leftAlignDataStyle = {
        ...dataStyle,
        alignment: { horizontal: 'left', vertical: 'middle' },
      };

      const sheet = workbook.addWorksheet('Bajo Vínculo');

      // Título
      sheet.mergeCells('A1:M1');
      sheet.getCell('A1').value = `Empresas con Bajo Vínculo (0-1 reuniones)${periodLabel || ''}`;
      sheet.getCell('A1').style = titleStyle;
      sheet.getRow(1).height = 25;

      // Resumen
      sheet.mergeCells('A2:M2');
      sheet.getCell('A2').value = `Total de empresas: ${fmt(lowLinkCompanies.length)}`;
      sheet.getRow(2).height = 20;

      // Espacio
      sheet.getRow(3).height = 10;

      // Encabezados
      sheet.getCell('A4').value = 'Posición';
      sheet.getCell('B4').value = 'Empresa';
      sheet.getCell('C4').value = 'RUT';
      sheet.getCell('D4').value = 'Industria';
      sheet.getCell('E4').value = 'Región / Comuna';
      sheet.getCell('F4').value = 'N° Reuniones';
      sheet.getCell('G4').value = 'Email Empresa';
      sheet.getCell('H4').value = 'Teléfono Fijo';
      sheet.getCell('I4').value = 'Teléfono Móvil';
      sheet.getCell('J4').value = 'Nombre Contacto';
      sheet.getCell('K4').value = 'Cargo';
      sheet.getCell('L4').value = 'Email Contacto';
      sheet.getCell('M4').value = 'Teléfono Contacto';
      sheet.getRow(4).eachCell((cell) => {
        cell.style = headerStyle;
      });
      sheet.getRow(4).height = 25;

      // Obtener datos completos de las empresas desde sum para tener información de contacto
      const companiesMap = new Map(sum.map((c) => [c.rut, c]));

      // Función auxiliar para obtener contactos de una empresa (optimizada)
      const getCompanyContacts = async (company, fullCompany) => {
        let contacts = [];
        try {
          // Intentar primero con business_name (que es el campo que usa el backend)
          const businessName = fullCompany.business_name || company.business_name;
          const tradeName = fullCompany.trade_name || company.trade_name || company.name;
          
          // Hacer todas las llamadas posibles en paralelo
          const contactPromises = [];
          
          if (businessName) {
            contactPromises.push(
              getContactsListByCompanySafe(businessName).catch(() => [])
            );
          }
          
          if (tradeName && tradeName !== businessName) {
            contactPromises.push(
              getContactsListByCompanySafe(tradeName).catch(() => [])
            );
          }
          
          if (businessName || tradeName) {
            contactPromises.push(
              getContactsListByCompanySafe({
                business_name: businessName || '',
                trade_name: tradeName || '',
              }).catch(() => [])
            );
          }
          
          // Ejecutar todas las llamadas en paralelo y tomar el primer resultado con contactos
          const results = await Promise.allSettled(contactPromises);
          for (const result of results) {
            if (result.status === 'fulfilled' && Array.isArray(result.value) && result.value.length > 0) {
              contacts = result.value;
              break; // Usar el primer resultado con contactos
            }
          }
        } catch (e) {
          console.error(`Error obteniendo contactos de ${company.name || company.rut}:`, e);
        }
        return contacts;
      };

      // Procesar empresas en lotes para obtener contactos en paralelo
      const batchSize = 15; // Procesar 15 empresas a la vez
      const companiesWithContacts = [];
      
      for (let i = 0; i < lowLinkCompanies.length; i += batchSize) {
        const batch = lowLinkCompanies.slice(i, i + batchSize);
        
        // Obtener contactos para todas las empresas del lote en paralelo
        const batchResults = await Promise.allSettled(
          batch.map(async (company) => {
            const fullCompany = companiesMap.get(company.rut) || {};
            const contacts = await getCompanyContacts(company, fullCompany);
            return { company, fullCompany, contacts };
          })
        );
        
        // Agregar resultados exitosos
        batchResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            companiesWithContacts.push(result.value);
          }
        });
        
        // Actualizar progreso
        if (loadingToast && loadingToast.isVisible && i + batchSize < lowLinkCompanies.length) {
          const progress = Math.round(((i + batchSize) / lowLinkCompanies.length) * 100);
          loadingToast.update({
            html: `Exportando lista de socios con bajo vínculo (0-1 reuniones)...<br/>Procesando empresas: ${Math.min(i + batchSize, lowLinkCompanies.length)} de ${lowLinkCompanies.length} (${progress}%)`
          });
        }
      }

      // Datos: una fila por contacto, o una fila con contacto vacío si no hay contactos
      let rowIndex = 5;
      let positionCounter = 1;

      for (const { company, fullCompany, contacts } of companiesWithContacts) {
        // Si hay contactos, crear una fila por cada contacto
        if (contacts.length > 0) {
          contacts.forEach((contact) => {
            const row = sheet.getRow(rowIndex);
            row.getCell(1).value = positionCounter;
            row.getCell(1).style = dataStyle;
            row.getCell(2).value =
              company.name || fullCompany.trade_name || fullCompany.business_name || '—';
            row.getCell(2).style = leftAlignDataStyle;
            row.getCell(3).value = company.rut || '—';
            row.getCell(3).style = dataStyle;
            row.getCell(4).value = company.industry || fullCompany.industry || '—';
            row.getCell(4).style = leftAlignDataStyle;
            row.getCell(5).value = `${company.region || fullCompany.region || '—'}${
              (company.district || fullCompany.district)
                ? ` / ${company.district || fullCompany.district}`
                : ''
            }`;
            row.getCell(5).style = leftAlignDataStyle;
            row.getCell(6).value = num(company.count, 0);
            row.getCell(6).numFmt = '#,##0';
            row.getCell(6).style = dataStyle;
            row.getCell(7).value = fullCompany.contact_email || '—';
            row.getCell(7).style = leftAlignDataStyle;
            row.getCell(8).value = fullCompany.landline_phone || '—';
            row.getCell(8).style = dataStyle;
            row.getCell(9).value = fullCompany.mobile_phone || '—';
            row.getCell(9).style = dataStyle;
            row.getCell(10).value = contact.contact_name || '—';
            row.getCell(10).style = leftAlignDataStyle;
            row.getCell(11).value = contact.title_job || '—';
            row.getCell(11).style = leftAlignDataStyle;
            row.getCell(12).value = contact.email || '—';
            row.getCell(12).style = leftAlignDataStyle;
            row.getCell(13).value = contact.phone_number || '—';
            row.getCell(13).style = dataStyle;

            row.height = 20;
            rowIndex++;
          });
        } else {
          // Si no hay contactos, crear una fila con campos de contacto vacíos
          const row = sheet.getRow(rowIndex);
          row.getCell(1).value = positionCounter;
          row.getCell(1).style = dataStyle;
          row.getCell(2).value =
            company.name || fullCompany.trade_name || fullCompany.business_name || '—';
          row.getCell(2).style = leftAlignDataStyle;
          row.getCell(3).value = company.rut || '—';
          row.getCell(3).style = dataStyle;
          row.getCell(4).value = company.industry || fullCompany.industry || '—';
          row.getCell(4).style = leftAlignDataStyle;
          row.getCell(5).value = `${company.region || fullCompany.region || '—'}${
            (company.district || fullCompany.district)
              ? ` / ${company.district || fullCompany.district}`
              : ''
          }`;
          row.getCell(5).style = leftAlignDataStyle;
          row.getCell(6).value = num(company.count, 0);
          row.getCell(6).numFmt = '#,##0';
          row.getCell(6).style = dataStyle;
          row.getCell(7).value = fullCompany.contact_email || '—';
          row.getCell(7).style = leftAlignDataStyle;
          row.getCell(8).value = fullCompany.landline_phone || '—';
          row.getCell(8).style = dataStyle;
          row.getCell(9).value = fullCompany.mobile_phone || '—';
          row.getCell(9).style = dataStyle;
          row.getCell(10).value = '—';
          row.getCell(10).style = leftAlignDataStyle;
          row.getCell(11).value = '—';
          row.getCell(11).style = leftAlignDataStyle;
          row.getCell(12).value = '—';
          row.getCell(12).style = leftAlignDataStyle;
          row.getCell(13).value = '—';
          row.getCell(13).style = dataStyle;

          row.height = 20;
          rowIndex++;
        }

        positionCounter++;
      }

      // Ajustar ancho de columnas
      sheet.getColumn(1).width = 12;
      sheet.getColumn(2).width = 35;
      sheet.getColumn(3).width = 15;
      sheet.getColumn(4).width = 25;
      sheet.getColumn(5).width = 30;
      sheet.getColumn(6).width = 15;
      sheet.getColumn(7).width = 30;
      sheet.getColumn(8).width = 18;
      sheet.getColumn(9).width = 18;
      sheet.getColumn(10).width = 25;
      sheet.getColumn(11).width = 25;
      sheet.getColumn(12).width = 30;
      sheet.getColumn(13).width = 18;

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Nombre del archivo
      const fileName = `Bajo_Vinculo_Empresas_${new Date()
        .toISOString()
        .split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      // Cerrar modal de carga
      loadingToast.close();

      Swal.fire(
        'Exportado',
        'Las empresas con bajo vínculo se han exportado correctamente.',
        'success'
      );
    } catch (error) {
      console.error('Error al exportar empresas con bajo vínculo:', error);
      // Cerrar modal de carga en caso de error
      if (typeof loadingToast !== 'undefined' && loadingToast.close) {
        loadingToast.close();
      }
      Swal.fire(
        'Error',
        'No se pudo exportar las empresas con bajo vínculo. Por favor, intenta nuevamente.',
        'error'
      );
    }
  };

  // Función para exportar top empresas a Excel
  const handleExportTopCompanies = async () => {
    // Mostrar confirmación con información
    const confirmResult = await Swal.fire({
      title: '¿Exportar lista de socios?',
      html: `
        <div class="text-left">
          <p class="mb-3">Se exportará la <strong>lista completa de socios ordenada por cantidad de reuniones</strong> (de mayor a menor).</p>
          <p class="text-sm text-gray-600">El archivo Excel incluirá todos los socios con reuniones en el período seleccionado.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, exportar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    // Mostrar modal de carga
    const loadingToast = Swal.fire({
      title: 'Exportando lista de socios...',
      html: 'Generando archivo Excel con la lista completa de socios ordenada por cantidad de reuniones (de mayor a menor). Por favor espera.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const workbook = new ExcelJS.Workbook();

      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0488AF' },
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        },
      };

      const titleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF09274A' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      };

      const dataStyle = {
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        },
      };

      const leftAlignDataStyle = {
        ...dataStyle,
        alignment: { horizontal: 'left', vertical: 'middle' },
      };

      const sheet = workbook.addWorksheet('Top Empresas');

      // Título
      sheet.mergeCells('A1:G1');
      sheet.getCell('A1').value = `Top Empresas (con actividad)${periodLabel || ''}`;
      sheet.getCell('A1').style = titleStyle;
      sheet.getRow(1).height = 25;

      // Resumen
      sheet.mergeCells('A2:G2');
      sheet.getCell('A2').value = `Total de empresas: ${fmt(withList.length)}`;
      sheet.getRow(2).height = 20;

      // Espacio
      sheet.getRow(3).height = 10;

      // Encabezados
      sheet.getCell('A4').value = 'Posición';
      sheet.getCell('B4').value = 'Empresa';
      sheet.getCell('C4').value = 'RUT';
      sheet.getCell('D4').value = 'Industria';
      sheet.getCell('E4').value = 'Región / Comuna';
      sheet.getCell('F4').value = 'N° Reuniones';
      sheet.getCell('G4').value = 'Relacionamiento con socios';
      sheet.getRow(4).eachCell((cell) => {
        cell.style = headerStyle;
      });
      sheet.getRow(4).height = 25;

      // Función para determinar relacionamiento
      const getRelacionamiento = (count) => {
        if (count >= 3) return 'Alto';
        if (count >= 1) return 'Medio';
        return 'Bajo';
      };

      // Datos
      withList.forEach((company, index) => {
        const row = sheet.getRow(index + 5);
        const meetingCount = num(company.count, 0);
        row.getCell(1).value = index + 1;
        row.getCell(1).style = dataStyle;
        row.getCell(2).value = company.name || '—';
        row.getCell(2).style = leftAlignDataStyle;
        row.getCell(3).value = company.rut || '—';
        row.getCell(3).style = dataStyle;
        row.getCell(4).value = company.industry || '—';
        row.getCell(4).style = leftAlignDataStyle;
        row.getCell(5).value = `${company.region || '—'}${
          company.district ? ` / ${company.district}` : ''
        }`;
        row.getCell(5).style = leftAlignDataStyle;
        row.getCell(6).value = meetingCount;
        row.getCell(6).numFmt = '#,##0';
        row.getCell(6).style = dataStyle;
        row.getCell(7).value = getRelacionamiento(meetingCount);
        row.getCell(7).style = dataStyle;

        row.height = 20;
      });

      // Ajustar ancho de columnas
      sheet.getColumn(1).width = 12;
      sheet.getColumn(2).width = 35;
      sheet.getColumn(3).width = 15;
      sheet.getColumn(4).width = 25;
      sheet.getColumn(5).width = 30;
      sheet.getColumn(6).width = 15;
      sheet.getColumn(7).width = 25;

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Nombre del archivo
      const fileName = `Top_Empresas_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      // Cerrar modal de carga
      loadingToast.close();

      Swal.fire(
        'Exportado',
        'Las top empresas se han exportado correctamente.',
        'success'
      );
    } catch (error) {
      console.error('Error al exportar top empresas:', error);
      // Cerrar modal de carga en caso de error
      if (typeof loadingToast !== 'undefined' && loadingToast.close) {
        loadingToast.close();
      }
      Swal.fire(
        'Error',
        'No se pudo exportar las top empresas. Por favor, intenta nuevamente.',
        'error'
      );
    }
  };

  // Nombres de meses para etiquetas
  const monthNames = {
    '1': 'Enero',
    '2': 'Febrero',
    '3': 'Marzo',
    '4': 'Abril',
    '5': 'Mayo',
    '6': 'Junio',
    '7': 'Julio',
    '8': 'Agosto',
    '9': 'Septiembre',
    '10': 'Octubre',
    '11': 'Noviembre',
    '12': 'Diciembre',
  };

  // Label dinámico para el período filtrado
  const periodLabel = useMemo(() => {
    const hasRange = yearFrom && monthFrom && yearTo && monthTo;
    const hasMonthRange = month && month.includes('-');

    if (hasRange) {
      return ` /rango ${monthNames[monthFrom] || monthFrom} ${yearFrom} - ${
        monthNames[monthTo] || monthTo
      } ${yearTo}`;
    } else if (year) {
      let label = ` /año ${year}`;
      if (month) {
        if (hasMonthRange) {
          const [fromMonth, toMonth] = month.split('-');
          label += ` /mes ${monthNames[fromMonth] || fromMonth} - ${
            monthNames[toMonth] || toMonth
          }`;
        } else {
          label += ` /mes ${monthNames[month] || month}`;
        }
      }
      return label;
    }
    return ' /total histórico';
  }, [year, month, yearFrom, monthFrom, yearTo, monthTo]);

  // Texto de rango de fecha para las tarjetas
  const dateRangeText = useMemo(() => {
    if (yearFrom && monthFrom && yearTo && monthTo) {
      return `${monthNames[monthFrom] || monthFrom} ${yearFrom} - ${monthNames[monthTo] || monthTo} ${yearTo}`;
    } else if (year) {
      if (month) {
        const hasMonthRange = month && month.includes('-');
        if (hasMonthRange) {
          const [fromMonth, toMonth] = month.split('-');
          return `${monthNames[fromMonth] || fromMonth} - ${monthNames[toMonth] || toMonth} ${year}`;
        } else {
          return `${monthNames[month] || month} ${year}`;
        }
      }
      return `Año ${year}`;
    }
    return 'Total histórico';
  }, [year, month, yearFrom, monthFrom, yearTo, monthTo]);

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      <div className="relative">
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
        <Banner
          title="Indicadores de relacionamiento"
          message="KPIs operativos para priorizar contacto"
        />
      </div>

      {/* Filtros - Arriba del todo */}
      {/* Indicador de filtros activos */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-5 h-5 text-blue-600 shrink-0" />
          <span className="text-sm font-semibold text-blue-900">Filtro de fecha aplicado:</span>
          {yearFrom && monthFrom && yearTo && monthTo ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
              Rango: {monthNames[monthFrom] || monthFrom} {yearFrom} - {monthNames[monthTo] || monthTo} {yearTo}
            </span>
          ) : year ? (
            <>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                Año: {year}
              </span>
              {month && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                  Mes: {month === '1' ? 'Enero' : month === '2' ? 'Febrero' : month === '3' ? 'Marzo' : month === '4' ? 'Abril' : month === '5' ? 'Mayo' : month === '6' ? 'Junio' : month === '7' ? 'Julio' : month === '8' ? 'Agosto' : month === '9' ? 'Septiembre' : month === '10' ? 'Octubre' : month === '11' ? 'Noviembre' : 'Diciembre'}
                </span>
              )}
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">
              Sin filtros (mostrando todos los años)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const currentYear = String(new Date().getFullYear());
              setYear(currentYear);
              setMonth('');
              setYearFrom('');
              setMonthFrom('');
              setYearTo('');
              setMonthTo('');
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-xs font-semibold tracking-wide uppercase shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors whitespace-nowrap"
            title="Filtrar solo por el año actual y limpiar otros filtros de fecha"
          >
            <Calendar size={16} />
            Filtrar año actual
          </button>
          <button
            onClick={() => {
              setYear('');
              setMonth('');
              setYearFrom('');
              setMonthFrom('');
              setYearTo('');
              setMonthTo('');
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors whitespace-nowrap"
            title="Limpiar todos los filtros de fecha para ver todos los años"
          >
            <XCircle size={16} />
            Limpiar todos
          </button>
        </div>
      </div>

      {/* Filtros desplegables */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Filtros de fecha desplegables */}
        <div
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
          }}
        >
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/50 transition-colors"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
            }}
          >
            <div className="flex items-center gap-1.5">
              <Filter size={16} className="text-[#0488AF]" />
              <span className="text-xs font-semibold text-gray-800">Filtros de fecha</span>
              <TooltipHelper text="Filtra las reuniones por año y/o rango de fechas. Seguirán apareciendo las tarjetas de todos los socios, pero el conteo de reuniones y los niveles de vínculo se ajustarán según el periodo seleccionado. Usa 'Limpiar' para ver todos los datos sin filtros." />
            </div>
            {filtersExpanded ? (
              <ChevronUp size={16} className="text-[#0488AF]" />
            ) : (
              <ChevronDown size={16} className="text-[#0488AF]" />
            )}
          </button>

          {filtersExpanded && (
            <div
              className="p-2 border-t border-gray-200/50"
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
              }}
            >
              <TimelineFilter
                year={year}
                month={month}
                yearOptions={yearOptions}
                onYearChange={setYear}
                onMonthChange={setMonth}
                yearFrom={yearFrom}
                monthFrom={monthFrom}
                yearTo={yearTo}
                monthTo={monthTo}
                onYearFromChange={setYearFrom}
                onMonthFromChange={setMonthFrom}
                onYearToChange={setYearTo}
                onMonthToChange={setMonthTo}
              />
            </div>
          )}
        </div>
      </div>

      <SectionTips
        section="los indicadores de relacionamiento"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo interpretar los indicadores de relacionamiento?"
        tipsList={[
          'Los indicadores muestran KPIs operativos para priorizar el contacto con empresas.',
          'Puedes filtrar por año, mes o rango de fechas para analizar períodos específicos.',
          'Las tarjetas muestran: empresas en sistema, empresas con reuniones, total de reuniones e inactivas.',
          'Haz clic en "Ver lista" para ver las empresas que cumplen cada criterio.',
          'Las empresas inactivas (≥ 60 días) requieren atención prioritaria.',
          'Puedes ver gráficos históricos haciendo clic en las tarjetas correspondientes.',
        ]}
        finalNote="Consejo: Revisa regularmente las empresas inactivas para mantener un vínculo activo."
        color="#0488AF"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412955/Manual_de_usuario_m%C3%B3dulo_de_vinculaci%C3%B3n_empresarial_af2jv3.pdf"
      />

      {/* KPI grid - 4 columnas iguales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Empresas en sistema"
          value={companies}
          icon={Building2}
          gradient="bg-white border-2 border-[#0488AF] text-[#09274A]"
          dateRange={dateRangeText}
        />
        <StatsCard
          title="Con reuniones"
          hint="≥ 1 en el año"
          value={withMeet}
          icon={Users2}
          gradient="bg-gradient-to-r from-[#0488AF] to-[#09274A] text-white"
          onClick={() => setShowWithMeet(true)}
          dateRange={dateRangeText}
          right={
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowWithMeet(true);
              }}
              className="text-xs px-2 py-1 rounded-md bg-[#0488AF] text-white hover:bg-[#037a98] transition-colors"
            >
              Ver lista
            </button>
          }
        />
        <StatsCard
          title="Total de reuniones"
          value={totalMeetings}
          icon={Activity}
          gradient="bg-white border-2 border-[#09274A] text-[#09274A]"
          dateRange={dateRangeText}
        />
        <StatsCard
          title="Inactivas mayor a 60 días"
          value={inactiveCount}
          icon={AlertTriangle}
          gradient="bg-white border-2 border-amber-300 text-amber-800"
          onClick={() => setShowInactive(true)}
          dateRange={dateRangeText}
          right={
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowInactive(true);
              }}
              className="text-xs px-2 py-1 rounded-md bg-[#0488AF] text-white hover:bg-[#037a98] transition-colors"
            >
              Ver lista
            </button>
          }
        />
      </div>


      {/* Gráfico comparativo */}
      <YearTotalsChart
        series={yearSeries}
        onShowHistorical={() => setShowHistoricalChart(true)}
      />

      {/* Listas inferiores · versión 2 más profesional */}
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
        {/* Top empresas */}
        <section className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          {/* Header */}
          <header
            className="flex items-center justify-between px-5 py-4 text-white"
            style={{
              background: 'linear-gradient(120deg,#0c3b66,#0488AF 80%)',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-semibold leading-tight text-white">
                  Top empresas (con actividad)
                </span>
                <span className="text-xs text-white/95 mt-0.5">
                  Mayor número de reuniones en el período seleccionado
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium bg-[#0488AF] backdrop-blur-sm border border-[#0488AF] text-white hover:bg-[#037a98] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleExportTopCompanies}
                disabled={withList.length === 0}
              >
                <Download size={13} />
                Exportar
              </button>
              <button
                className="rounded-full px-3 py-1.5 text-[11px] font-medium border border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                onClick={() => setShowTopAll(true)}
              >
                Ver todas
              </button>
            </div>
          </header>

          {/* Lista */}
          <div className="px-5 py-4 space-y-2.5">
            {topPreview.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-5">
                No hay empresas con reuniones en el período.
              </div>
            ) : (
              topPreview.map((r, idx) => (
                <div
                  key={r.rut}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-slate-100 px-3 py-2.5 hover:bg-white hover:border-[#0488AF]/30 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full border border-[#0488AF33] bg-white flex items-center justify-center text-[11px] font-semibold text-[#0488AF]">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {r.name}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">
                        {r.industry || '—'}
                        {r.region ? ` • ${r.region}` : ''}
                        {r.district ? ` / ${r.district}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center rounded-full border border-[#BAE6FD] bg-[#E0F2FE] px-3 py-1 text-[11px] font-semibold text-[#075985] whitespace-nowrap">
                    {fmt(r.count)} {r.count === 1 ? 'reunión' : 'reuniones'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Bajo vínculo */}
        <section className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          {/* Header */}
          <header
            className="flex items-center justify-between px-5 py-4 text-white"
            style={{
              background: 'linear-gradient(120deg,#0488AF,#0c3b66 80%)',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-semibold leading-tight text-white">
                  Bajo vínculo (0–1{periodLabel})
                </span>
                <span className="text-xs text-white/95 mt-0.5">
                  Empresas con muy pocas reuniones en el período
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleExportLowLinkCompanies}
                disabled={!(ana?.ranking || []).some((r) => num(r.count, 0) <= 1)}
              >
                <Download size={13} />
                Exportar
              </button>
              <button
                className="rounded-full px-3 py-1.5 text-[11px] font-medium border border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                onClick={() => setShowBottomAll(true)}
              >
                Ver todas
              </button>
            </div>
          </header>

          {/* Lista */}
          <div className="px-5 py-4 space-y-2.5">
            {(ana?.ranking || []).filter((r) => num(r.count, 0) <= 1).length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-5">
                No hay empresas con bajo vínculo en el período.
              </div>
            ) : (
              (ana?.ranking || [])
                .map((r) => ({ ...r, count: num(r.count, 0) }))
                .filter((r) => r.count <= 1)
                .slice(0, 5)
                .map((r, idx) => {
                  const isZero = r.count === 0;
                  return (
                  <div
                    key={r.rut}
                      className={`flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 border transition-all ${
                        isZero
                          ? 'border-red-300 bg-red-50 hover:border-red-400 hover:bg-red-100 hover:shadow-md'
                          : 'border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100 hover:shadow-md'
                      }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                            isZero
                              ? 'bg-white text-red-600 border border-red-200'
                              : 'bg-white text-amber-700 border border-amber-200'
                        }`}
                      >
                        {idx + 1}
                      </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                          {r.name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {r.industry || '—'}
                          {r.region ? ` • ${r.region}` : ''}
                          {r.district ? ` / ${r.district}` : ''}
                        </div>
                      </div>
                    </div>
                      <div className="flex-shrink-0">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap border ${
                            isZero
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-amber-50 text-amber-800 border-amber-200'
                      }`}
                    >
                      {fmt(r.count)} {r.count === 1 ? 'reunión' : 'reuniones'}
                        </span>
                    </div>
                  </div>
                  );
                })
            )}
          </div>
        </section>
      </div>

      {/* Modales */}
      <Modal
        open={showWithMeet}
        title={`Empresas con reuniones ${year ? `(${year})` : ''}`}
        onClose={() => setShowWithMeet(false)}
      >
        {withList.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-5">
            No hay empresas con reuniones en el período.
          </div>
        ) : (
          <div className="space-y-2.5">
            {withList.map((r, idx) => (
              <div
                key={r.rut}
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-slate-100 px-3 py-2.5 hover:bg-white hover:border-[#0488AF]/30 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full border border-[#0488AF33] bg-white flex items-center justify-center text-[11px] font-semibold text-[#0488AF]">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {r.name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      {r.industry || '—'}
                      {r.region ? ` • ${r.region}` : ''}
                      {r.district ? ` / ${r.district}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center rounded-full border border-[#BAE6FD] bg-[#E0F2FE] px-3 py-1 text-[11px] font-semibold text-[#075985] whitespace-nowrap">
                    {fmt(r.count)} {r.count === 1 ? 'reunión' : 'reuniones'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={showInactive}
        title="Inactivas ≥ 60 días (según último contacto)"
        onClose={() => setShowInactive(false)}
      >
        {inactivas.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-5">
            No hay empresas inactivas en el período.
          </div>
        ) : (
          <div className="space-y-2.5">
            {inactivas.map((c, idx) => (
              <div
                key={c.rut}
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-slate-100 px-3 py-2.5 hover:bg-white hover:border-[#0488AF]/30 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full border border-[#0488AF33] bg-white flex items-center justify-center text-[11px] font-semibold text-[#0488AF]">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {c.name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      Últ. reunión:{' '}
                      {c.lastMeetingAt ? formatDate(c.lastMeetingAt) : 'Sin reuniones'}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center rounded-full border border-[#BAE6FD] bg-[#E0F2FE] px-3 py-1 text-[11px] font-semibold text-[#075985] whitespace-nowrap">
                    {formatDays(c.days)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={showTopAll}
        title="Top empresas (con actividad)"
        onClose={() => setShowTopAll(false)}
      >
        {withList.length ? (
          <div className="space-y-2.5">
            {withList.map((r, idx) => (
              <div
                key={r.rut}
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-slate-100 px-3 py-2.5 hover:bg-white hover:border-[#0488AF]/30 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full border border-[#0488AF33] bg-white flex items-center justify-center text-[11px] font-semibold text-[#0488AF]">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {r.name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      {r.industry || '—'}
                      {r.region ? ` • ${r.region}` : ''}
                      {r.district ? ` / ${r.district}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center rounded-full border border-[#BAE6FD] bg-[#E0F2FE] px-3 py-1 text-[11px] font-semibold text-[#075985] whitespace-nowrap">
                  {fmt(r.count)} {r.count === 1 ? 'reunión' : 'reuniones'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 text-center py-5">
            No hay empresas con reuniones en el período.
          </div>
        )}
      </Modal>

      <Modal
        open={showBottomAll}
        title={`Bajo vínculo (0–1${periodLabel})`}
        onClose={() => setShowBottomAll(false)}
      >
        {(ana?.ranking || []).some((r) => num(r.count, 0) <= 1) ? (
          <div className="space-y-2.5">
            {(ana?.ranking || [])
              .map((r) => ({ ...r, count: num(r.count, 0) }))
              .filter((r) => r.count <= 1)
              .sort((a, b) => a.count - b.count)
              .map((r, idx) => {
                const isZero = r.count === 0;
                return (
                <div
                  key={r.rut}
                    className={`flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 border transition-all ${
                      isZero
                        ? 'border-red-300 bg-red-50 hover:border-red-400 hover:bg-red-100 hover:shadow-md'
                        : 'border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100 hover:shadow-md'
                    }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                          isZero
                            ? 'bg-white text-red-600 border border-red-200'
                            : 'bg-white text-amber-700 border border-amber-200'
                      }`}
                    >
                      {idx + 1}
                    </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                        {r.name}
                      </div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                        {r.industry || '—'}
                        {r.region ? ` • ${r.region}` : ''}
                        {r.district ? ` / ${r.district}` : ''}
                      </div>
                    </div>
                  </div>
                    <div className="flex-shrink-0">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap border ${
                          isZero
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-amber-50 text-amber-800 border-amber-200'
                    }`}
                  >
                    {fmt(r.count)} {r.count === 1 ? 'reunión' : 'reuniones'}
                      </span>
                  </div>
                </div>
                );
              })}
          </div>
        ) : (
          <div className="text-sm text-slate-500 text-center py-5">
            No hay empresas con bajo vínculo en el período.
          </div>
        )}
      </Modal>

      {/* Modal de histórico completo */}
      <HistoricalChartModal
        open={showHistoricalChart}
        onClose={() => setShowHistoricalChart(false)}
        excludeRut={excludeRut}
        year={year}
        month={month}
        yearFrom={yearFrom}
        monthFrom={monthFrom}
        yearTo={yearTo}
        monthTo={monthTo}
        maxDataYear={maxDataYear}
      />
    </div>
  );
}

/* -------- Modal -------- */
function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center overflow-auto">
        <div className="w-full max-w-5xl mx-auto my-10">
          <div className="rounded-2xl overflow-hidden border bg-white shadow-xl">
            <div className="px-5 py-3 bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white flex items-center justify-between">
              <div className="font-semibold">{title}</div>
              <button
                onClick={onClose}
                className="text-white/90 hover:text-white text-sm font-medium"
              >
                Cerrar
              </button>
            </div>
            <div className="p-5">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
